import React, { useContext, useEffect, useRef, useCallback } from 'react'
import { FaBars } from 'react-icons/fa'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory, useLocation } from 'react-router-dom'
import paths from '../../routes/paths'
import { AppContext } from '../../context/AppContext'
import { useAuthentication } from '../../customHooks/useAuthentication'
import { FiUser, FiLogOut, FiMaximize, FiMinimize, FiHelpCircle, FiShield } from 'react-icons/fi'
import { Divider, ListItem, ListItemAvatar, ListItemText, IconButton, MenuItem, Menu, Tooltip } from '@material-ui/core'
import { FlagPL, FlagES, FlagUS } from './Flags'
import HeaderNotifications from './HeaderNotifications'
import AvatarDefault from '../../assets/img/avatar-default.png'
import { getUserRoleLabel } from '../../utils/userRole'
import PDFModal from '../ModalPdf'
import { fetchUserGuidePdfBlob } from '../../services/docsService'
import { logoutSession } from '../../utils/sessionAuth'
import { useAccess } from '../../access/AccessContext'
import { routeAllowed } from '../../access/accessPolicy'
import FeatureSearch from './FeatureSearch'
import './header.style.css'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
    background: 'transparent',
  },
  title: {
    flexGrow: 1,
  },
  menuItem: {
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
    gap: '8px !important',
    minWidth: '140px',
    borderRadius: '8px',
    margin: '2px 4px',
    padding: '8px 12px',
    transition: 'all 0.2s ease-in-out',
    color: '#334155',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
      color: '#334155',
    },
  },
  menuItemActive: {
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
    gap: '8px !important',
    minWidth: '140px',
    borderRadius: '8px',
    margin: '2px 4px',
    padding: '8px 12px',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: 'rgba(232, 96, 28, 0.08) !important',
    color: '#e8601c !important',
    '&:hover': {
      backgroundColor: 'rgba(232, 96, 28, 0.12) !important',
      color: '#e8601c !important',
    },
  },
  userMenuItem: {
    borderRadius: '8px',
    margin: '2px 4px',
    padding: '8px 12px',
    transition: 'all 0.2s ease-in-out',
    color: '#334155',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
      color: '#334155',
    },
  },
}))

function headerWarehouseLabel(user, access, selectedWarehouseId) {
  const enforced = access?.profileEnforced === true && access?.role !== 'admin' && !access?.isAdmin
  if (!enforced) return user?.warehouse?.name || ''
  const rows = Array.isArray(access.warehouses) ? access.warehouses : []
  const selected = rows.find((row) => Number(row.id) === Number(selectedWarehouseId))
  return selected?.name || 'Sin warehouse autorizado'
}

const Header = () => {
  const { formatMessage: trans } = useIntl()
  const { navigationExpanded, changeNavigationVisible, locale, changeLocale } = useContext(AppContext)
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  const history = useHistory()
  const location = useLocation()

  const { userInfo: user } = useAuthentication()
  const userRoleLabel = getUserRoleLabel(user, trans)
  const userDisplayName = user?.name || trans({ id: 'user', defaultMessage: 'User' })

  const [anchorElLang, setAnchorElLang] = React.useState(null)
  const [isLangHovered, setIsLangHovered] = React.useState(false)
  const [isUserHovered, setIsUserHovered] = React.useState(false)
  const openLang = Boolean(anchorElLang)

  const handleMenuLang = (event) => {
    setAnchorElLang(event.currentTarget)
  }

  const handleCloseLang = () => {
    setAnchorElLang(null)
  }

  const handleSelectLang = (lang) => {
    changeLocale(lang)
    handleCloseLang()
  }

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const logout = async () => {
    await logoutSession({ redirect: false })
    history.replace('/')
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const [isFullScreen, setFullScreen] = React.useState(false)
  const [helpModalOpen, setHelpModalOpen] = React.useState(false)
  const [helpPdfUrl, setHelpPdfUrl] = React.useState(null)
  const [helpPdfLoading, setHelpPdfLoading] = React.useState(false)
  const [helpPdfError, setHelpPdfError] = React.useState(null)
  const helpPdfUrlRef = useRef(null)
  const helpModalOpenRef = useRef(false)

  const handleCloseHelpModal = () => {
    setHelpModalOpen(false)
  }

  const resetHelpPdfState = useCallback(() => {
    if (helpModalOpenRef.current) return
    if (helpPdfUrlRef.current) {
      URL.revokeObjectURL(helpPdfUrlRef.current)
      helpPdfUrlRef.current = null
    }
    setHelpPdfUrl(null)
    setHelpPdfError(null)
    setHelpPdfLoading(false)
  }, [])

  useEffect(() => {
    helpModalOpenRef.current = helpModalOpen
  }, [helpModalOpen])

  useEffect(() => {
    if (!helpModalOpen) return

    let cancelled = false
    setHelpPdfLoading(true)
    setHelpPdfError(null)

    fetchUserGuidePdfBlob()
      .then((blob) => {
        if (cancelled) return
        const objectUrl = URL.createObjectURL(blob)
        helpPdfUrlRef.current = objectUrl
        setHelpPdfUrl(objectUrl)
      })
      .catch((error) => {
        if (cancelled || error?.response?.status === 401) return
        setHelpPdfError(
          error?.message ||
            trans({
              id: 'help_pdf_load_error',
              defaultMessage: 'Could not load the user guide.',
            }),
        )
      })
      .finally(() => {
        if (!cancelled) setHelpPdfLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [helpModalOpen, trans])
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      if (document.exitFullscreen) document.exitFullscreen()
    }
    setFullScreen(!isFullScreen)
  }

  const isPos = location.pathname === '/pos'
  const companyName = user?.company?.name
  const { access, selectedWarehouseId } = useAccess()
  const warehouseName = headerWarehouseLabel(user, access, selectedWarehouseId)
  const showAccount = routeAllowed(paths.settings.account, access)

  return (
    <header>
      <div className={classes.root}>
        <div className="topbar">
          <div className="d-flex align-items-center gap-2">
            <button
              className="toggle"
              onClick={() => changeNavigationVisible(!navigationExpanded)}
              aria-expanded={!navigationExpanded}
              aria-label={navigationExpanded ? 'Expand sidebar navigation' : 'Collapse sidebar navigation'}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                padding: 0,
              }}
            >
              <FaBars />
            </button>
            {companyName && <div className="warehouse__name d-none d-sm-block">{companyName}</div>}
            {companyName && warehouseName && <span className="separator d-none d-md-block">/</span>}
            {warehouseName && <div className="warehouse__name d-none d-sm-block">{warehouseName}</div>}
          </div>
          <div style={{ width: '100%', minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                minWidth: 0,
              }}
            >
              <FeatureSearch />
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >
              <Tooltip title={trans({ id: 'help', defaultMessage: 'Help' })} arrow>
                <IconButton onClick={() => setHelpModalOpen(true)} color="inherit" className="p-1" aria-label={trans({ id: 'help', defaultMessage: 'Help' })}>
                  <FiHelpCircle style={{ fontSize: '1.25rem', color: '#64748b' }} />
                </IconButton>
              </Tooltip>

              {isPos && (
                <IconButton onClick={toggleFullScreen} color="inherit" className="p-1">
                  {isFullScreen ? <FiMinimize /> : <FiMaximize />}
                </IconButton>
              )}
              <HeaderNotifications user={user} />

              {/* Language Switcher */}
              <button
                aria-haspopup="true"
                onClick={handleMenuLang}
                onMouseEnter={() => setIsLangHovered(true)}
                onMouseLeave={() => setIsLangHovered(false)}
                className="d-flex align-items-center justify-content-center gap-2"
                style={{
                  backgroundColor: isLangHovered ? 'rgba(232, 96, 28, 0.3)' : '#ffffff',
                  border: isLangHovered ? '1.5px solid #e8601c' : '1.5px solid #cbd5e1',
                  borderRadius: '0.3rem',
                  padding: '6px 12px',
                  minWidth: '70px',
                  height: '36px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                  transition: 'all 0.2s ease-in-out',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                {locale === 'es' ? <FlagES /> : locale === 'pl' ? <FlagPL /> : <FlagUS />}
                <span className="fw-semibold text-uppercase" style={{ fontSize: '12px', color: isLangHovered ? '#e8601c' : '#334155', lineHeight: '1', transition: 'all 0.2s ease-in-out' }}>
                  {locale}
                </span>
                <i className="fas fa-chevron-down" style={{ fontSize: '9px', color: isLangHovered ? '#e8601c' : '#64748b', transition: 'all 0.2s ease-in-out' }}></i>
              </button>
              <Menu
                id="menu-lang"
                PaperProps={{ style: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)', border: '1px solid #e2e8f0', padding: '4px' } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={handleCloseLang}
                getContentAnchorEl={null}
                anchorEl={anchorElLang}
                open={openLang}
              >
                <MenuItem onClick={() => handleSelectLang('en')} className={locale === 'en' ? classes.menuItemActive : classes.menuItem}>
                  <div className="d-flex align-items-center gap-2">
                    <FlagUS />
                    <span className="font-inter fs-small fw-semibold">{trans({ id: 'english', defaultMessage: 'English' })}</span>
                  </div>
                  {locale === 'en' && <i className="fas fa-check" style={{ fontSize: '10px', color: '#e8601c' }}></i>}
                </MenuItem>
                <MenuItem onClick={() => handleSelectLang('es')} className={locale === 'es' ? classes.menuItemActive : classes.menuItem}>
                  <div className="d-flex align-items-center gap-2">
                    <FlagES />
                    <span className="font-inter fs-small fw-semibold">{trans({ id: 'spanish', defaultMessage: 'Spanish' })}</span>
                  </div>
                  {locale === 'es' && <i className="fas fa-check" style={{ fontSize: '10px', color: '#e8601c' }}></i>}
                </MenuItem>
                <MenuItem onClick={() => handleSelectLang('pl')} className={locale === 'pl' ? classes.menuItemActive : classes.menuItem}>
                  <div className="d-flex align-items-center gap-2">
                    <FlagPL />
                    <span className="font-inter fs-small fw-semibold">{trans({ id: 'polish', defaultMessage: 'Polish' })}</span>
                  </div>
                  {locale === 'pl' && <i className="fas fa-check" style={{ fontSize: '10px', color: '#e8601c' }}></i>}
                </MenuItem>
              </Menu>

              <Tooltip title={`${userDisplayName} · ${userRoleLabel}`} arrow>
                <button
                  aria-haspopup="true"
                  onClick={handleMenu}
                  onMouseEnter={() => setIsUserHovered(true)}
                  onMouseLeave={() => setIsUserHovered(false)}
                  className="header-user-trigger d-flex align-items-center gap-2 border-0 bg-transparent"
                  style={{
                    borderRadius: '20px',
                    padding: '3px 8px',
                    backgroundColor: isUserHovered ? 'rgba(0,0,0,0.04)' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    outline: 'none',
                    cursor: 'pointer',
                    height: '36px',
                    maxWidth: '220px',
                  }}
                >
                  <figure className="avatar__container mb-0" style={{ width: '30px', height: '30px', minWidth: '30px', minHeight: '30px', inlineSize: '30px', blockSize: '30px' }}>
                    <img src={AvatarDefault} alt="Avatar" style={{ blockSize: '100%', transform: 'none' }} />
                  </figure>
                  <div className="d-none d-md-flex align-items-center gap-1 header-user-trigger__content">
                    <div className="header-user-trigger__text d-flex flex-column align-items-start">
                      <span className="header-user-trigger__name">{userDisplayName}</span>
                      <span className="header-user-trigger__role">{userRoleLabel}</span>
                    </div>
                    <i className="fas fa-chevron-down ms-1 header-user-trigger__chevron" aria-hidden="true"></i>
                  </div>
                </button>
              </Tooltip>
              <Menu
                id="menu-appbar"
                PaperProps={{ style: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)', border: '1px solid #e2e8f0', padding: '4px' } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                getContentAnchorEl={null}
                onClose={handleClose}
                anchorEl={anchorEl}
                open={open}
              >
                <ListItem style={{ inlineSize: '250px' }}>
                  <ListItemAvatar>
                    <figure className="avatar__container mb-0" style={{ width: '40px', height: '40px' }}>
                      <img src={AvatarDefault} alt="Avatar" />
                    </figure>
                  </ListItemAvatar>
                  <ListItemText
                    secondaryTypographyProps={{ noWrap: true, style: { fontSize: '12px', color: '#64748b' } }}
                    primaryTypographyProps={{ noWrap: true, style: { fontWeight: 600, color: '#334155' } }}
                    primary={userDisplayName}
                    secondary={userRoleLabel}
                  />
                </ListItem>
                <Divider style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }} className="mb-2" />
                <MenuItem
                  onClick={() => {
                    handleClose()
                    history.push(paths.profile)
                  }}
                  className={classes.userMenuItem}
                >
                  <FiUser className="me-2" />
                  <span className="font-inter fs-small">{trans({ id: 'my_profile', defaultMessage: 'My Profile' })}</span>
                </MenuItem>
                {showAccount ? (
                  <MenuItem
                    onClick={() => {
                      handleClose()
                      history.push(paths.settings.account)
                    }}
                    className={classes.userMenuItem}
                  >
                    <FiShield className="me-2" />
                    <span className="font-inter fs-small">{trans({ id: 'account', defaultMessage: 'Account' })}</span>
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={() => {
                    handleClose()
                    logout()
                  }}
                  className={classes.userMenuItem}
                >
                  <FiLogOut className="me-2" />
                  <span className="font-inter fs-small">{trans({ id: 'logout', defaultMessage: 'Logout' })}</span>
                </MenuItem>
              </Menu>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PDFModal
        title={trans({
          id: 'help_user_guide',
          defaultMessage: 'User Guide',
        })}
        isOpen={helpModalOpen}
        closeModal={handleCloseHelpModal}
        onExited={resetHelpPdfState}
        pdfUrl={helpPdfUrl}
        isLoading={helpPdfLoading}
        loadError={helpPdfError}
      />
    </header>
  )
}

export default Header
